/**
 * API hook for the therapist's custom questionnaire files (Plan 2, Task 1).
 *
 * Backend (Plan 1, live on main): POST /questionnaires/files/upload-url ->
 * presigned S3 PUT; POST /questionnaires/files/{id}/parse (async, mirrors
 * the parse_assessment worker pattern) + GET poll by run_id; GET
 * /questionnaires/files (list templates); PUT /questionnaires/files/{id}
 * (save the reviewed instrument -> "ready"); DELETE /questionnaires/files/{id}.
 *
 * The parse GET only reports the ASYNC_RUN's own progress ("running" ->
 * "done" | "error") — it does NOT carry the parsed instrument. The
 * template row's own `status` field is the source of truth (spec
 * docs/superpowers/specs/2026-07-05-questionnaire-files-dual-channel-design.md
 * §2), so pollParse re-fetches the template list once the run finishes to
 * read the row's real terminal status + parsed_instrument/parse_error.
 *
 * All errors are loud: HTTP failures throw a QuestionnaireFilesApiError
 * carrying both the status code and the parsed body (CLAUDE.md — no
 * silent/degraded handling); callers (dialog gating, shelf UI) can branch
 * on `.status` (e.g. 402 plan_required, 409 already parsing).
 */
import { useCallback } from "react";
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import { reportPaymentRequired } from "../../lib/api-client";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// The worker's own timeout is 300s (parse_file.py); give polling headroom.
const DEFAULT_POLL_MAX_MS = 6 * 60_000;
const DEFAULT_POLL_STEP_MS = 2_000;

export class QuestionnaireFilesApiError extends Error {
  status: number;
  body: any;

  constructor(status: number, body: any) {
    super((body && typeof body === "object" && body.error) || `HTTP ${status}`);
    this.name = "QuestionnaireFilesApiError";
    this.status = status;
    this.body = body;
  }
}

export interface CustomInstrumentItem {
  id: string;
  text: string;
}

export interface CustomInstrumentScaleStep {
  value: number;
  label: string;
}

export interface CustomInstrument {
  name: string;
  timeframe?: string;
  lang: "en" | "hu";
  items: CustomInstrumentItem[];
  scale: CustomInstrumentScaleStep[];
}

export type QuestionnaireTemplateStatus = "parsing" | "needs_review" | "ready" | "parse_failed";

export interface QuestionnaireTemplate {
  fileId: string;
  fileName: string;
  contentType?: string;
  size?: number;
  status: QuestionnaireTemplateStatus;
  parsedInstrument?: CustomInstrument;
  parseError?: string;
  instrumentVersion?: number;
  createdAt?: number;
  updatedAt?: number;
  parsedAt?: number;
}

export interface RequestUploadResult {
  fileId: string;
  uploadUrl: string;
  expiresIn: number;
}

export interface ParseResult {
  status: "needs_review" | "ready" | "parse_failed";
  parsedInstrument?: CustomInstrument;
  parseError?: string;
}

function mapTemplate(raw: any): QuestionnaireTemplate {
  return {
    fileId: raw.file_id,
    fileName: raw.file_name,
    contentType: raw.content_type,
    size: raw.size,
    status: raw.status,
    parsedInstrument: raw.parsed_instrument,
    parseError: raw.parse_error,
    instrumentVersion: raw.instrument_version,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    parsedAt: raw.parsed_at,
  };
}

export function useQuestionnaireFiles() {
  const { ensureToken } = useAuth();

  const apiJson = useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const token = await ensureToken();
    const resp = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) },
    });

    const text = await resp.text();
    let data: any = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      console.error("[useQuestionnaireFiles] JSON parse failed:", { path, status: resp.status, text });
      throw new QuestionnaireFilesApiError(resp.status, { error: "non-JSON response", raw: text });
    }

    if (!resp.ok) {
      reportPaymentRequired(resp.status, data); // every endpoint here is Pro-gated (402 -> app-wide upgrade toast)
      throw new QuestionnaireFilesApiError(resp.status, data);
    }
    return data as T;
  }, [ensureToken]);

  const list = useCallback(async (signal?: AbortSignal): Promise<QuestionnaireTemplate[]> => {
    const data = await apiJson<{ files?: any[] }>("/questionnaires/files", { signal });
    return (Array.isArray(data.files) ? data.files : []).map(mapTemplate);
  }, [apiJson]);

  // The returned presigned PUT URL is signed for ContentType
  // application/octet-stream (get_upload_url.py). Task 2's S3 PUT MUST NOT
  // set a Content-Type header (or set exactly application/octet-stream) —
  // any other value mismatches the signature and S3 rejects with
  // SignatureDoesNotMatch. No content_type is sent here because the backend
  // ignores it.
  const requestUpload = useCallback(async (
    fileName: string,
    size: number,
  ): Promise<RequestUploadResult> => {
    const data = await apiJson<{ file_id: string; upload_url: string; expires_in: number }>(
      "/questionnaires/files/upload-url",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_name: fileName, size }),
      },
    );
    return { fileId: data.file_id, uploadUrl: data.upload_url, expiresIn: data.expires_in };
  }, [apiJson]);

  const startParse = useCallback(async (fileId: string, fileName?: string): Promise<string> => {
    const data = await apiJson<{ run_id: string }>(
      `/questionnaires/files/${encodeURIComponent(fileId)}/parse`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fileName ? { file_name: fileName } : {}),
      },
    );
    return data.run_id;
  }, [apiJson]);

  const pollParse = useCallback(async (
    fileId: string,
    runId: string,
    opts?: { onUpdate?: (status: "parsing") => void; maxMs?: number; stepMs?: number; signal?: AbortSignal },
  ): Promise<ParseResult> => {
    const maxMs = opts?.maxMs ?? DEFAULT_POLL_MAX_MS;
    const stepMs = opts?.stepMs ?? DEFAULT_POLL_STEP_MS;
    const signal = opts?.signal;
    const t0 = Date.now();

    for (;;) {
      const qs = new URLSearchParams({ run_id: runId });
      const data = await apiJson<{ run_id: string; status: string; result?: string; error?: string }>(
        `/questionnaires/files/${encodeURIComponent(fileId)}/parse?${qs.toString()}`,
        { signal },
      );

      if (data.status === "running") {
        opts?.onUpdate?.("parsing");
        if (Date.now() - t0 >= maxMs) {
          throw new Error("Parse timed out (still running).");
        }
        await sleep(stepMs);
        continue;
      }

      if (data.status === "error") {
        return { status: "parse_failed", parseError: data.error || "parse failed" };
      }

      if (data.status === "done") {
        // The run result only echoes {file_id, status}; the parsed
        // instrument/error live on the template row — re-fetch it.
        const templates = await list(signal);
        const row = templates.find((t) => t.fileId === fileId);
        if (!row) {
          throw new Error(`Parsed template not found after completion (file_id=${fileId})`);
        }
        if (row.status === "needs_review" || row.status === "ready") {
          return { status: row.status, parsedInstrument: row.parsedInstrument };
        }
        if (row.status === "parse_failed") {
          return { status: "parse_failed", parseError: row.parseError };
        }
        throw new Error(`Unexpected template status after parse: ${row.status}`);
      }

      throw new Error(`Unexpected parse run status: ${data.status}`);
    }
  }, [apiJson, list]);

  const saveInstrument = useCallback(async (
    fileId: string,
    instrument: CustomInstrument,
  ): Promise<QuestionnaireTemplate> => {
    const data = await apiJson<{ file: any }>(
      `/questionnaires/files/${encodeURIComponent(fileId)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instrument }),
      },
    );
    return mapTemplate(data.file);
  }, [apiJson]);

  const remove = useCallback(async (fileId: string): Promise<void> => {
    await apiJson(`/questionnaires/files/${encodeURIComponent(fileId)}`, { method: "DELETE" });
  }, [apiJson]);

  return { list, requestUpload, startParse, pollParse, saveInstrument, remove };
}
